import { SpacingToken } from '@livechat/design-system-react-components'
import { useState } from 'react'
import { object, bool, func, oneOfType } from 'prop-types'
import { Link } from 'gatsby'
import useMedia from 'use-media'
import { css } from '@emotion/react'

import SearchIcon from '@/assets/icons/search.inline.svg'
import HamburgerIcon from '@/assets/icons/hamburger.inline.svg'

import useHydrationContext from '@/hooks/useHydrationContext'
import { canUseWindow } from '@/utils/browser'
import { URLS, APPS_LIST_STORAGE_KEY } from '@/constants'
import mediaQueries from '@/layout/media-queries'
import {
  isAppInstalled,
  isAuthorized as isUserAuthorized,
  isUserInitialized,
} from '@/store/selectors'
import { useTypedSelector } from '@/store/hooks'
import useParamSelector from '@/hooks/useParamSelector'
import { isHelpDesk } from '@/utils'

import MPLink from '../MPLink'
import PagesMenu from './PagesMenu'
import Account from './Account'
import Logo from './Logo'
import NavLink from './NavLink'
import Search from './Search'
import MobileMenu from './MobileMenu'
import BreadcrumbsWrapper from './BreadcrumbsWrapper'
import Cart from './Cart'
import MobileSearch from './MobileSearch'

const navigationWrapper = ({ spacing }) => css`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 0px;
  max-width: 1376px;
  margin: 0 auto;

  ${mediaQueries.smallScreen()} {
    padding: 0 ${spacing.m};
  }

  ${mediaQueries.mediumScreen(100)} {
    height: 90px;
  }

  ${mediaQueries.largeScreen()} {
    padding: 0 ${spacing.l};
  }

  ${mediaQueries.xlargeScreen()} {
    padding: 0 ${spacing.xl};
  }
`

const menuWrapperCss = (isExpanded) => css`
  display: none;
  opacity: ${isExpanded ? 0 : 1};
  pointer-events: ${isExpanded ? 'none' : 'auto'};
  transition: opacity 0.2s ease;

  .page-item {
    margin: 0 10px;
  }

  ${mediaQueries.mediumScreen(100)} {
    display: block;
  }
`

const actionsWrapperCss = (isAuthorized) => css`
  width: ${isAuthorized ? '140px' : '350px'};
  height: 100%;
  display: none;
  position: relative;

  > div {
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
  }

  ${mediaQueries.mediumScreen(100)} {
    display: flex;
    align-items: center;
  }

  ${mediaQueries.xlargeScreen()} {
    width: 350px;
  }
`

const actionsContentCss = css`
  > div {
    margin-right: 26px;

    &:last-of-type {
      margin-right: 0;
    }
  }
`

const mobileActionsWrapper = (isAuthorized) => css`
  display: flex;
  column-gap: var(${SpacingToken.Spacing7});

  ${mediaQueries.mediumScreen(isAuthorized ? 100 : 170)} {
    display: none;
  }
`

const mobileActionLoaderCss = css`
  display: flex;
  column-gap: 18px;
  height: 40px;
  align-items: center;

  ${mediaQueries.mediumScreen(170)} {
    display: none;
  }

  svg:hover {
    cursor: progress;
  }

  .icon-hamburger {
    padding: 8px;
    display: flex;
    align-items: center;
  }
`

const pagesMenuCss = css`
  @media (max-width: ${mediaQueries.MEDIUM}) {
    display: none;
  }
`

const logoLabelCss = css`
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  margin: 0;
`

const loaderCss = (theme) => css`
  display: flex;
  justify-self: end;

  > div {
    margin: 0 5px;
    height: 24px;
    background-color: ${theme.colors.mystic};
  }
  .circle {
    width: 24px;
    border-radius: 50%;
  }
  .rectangle {
    width: 60px;
    border-radius: 4px;
  }
`

const shopifyAppId = process.env.GATSBY_SHOPIFY_APP_ID || ''

const Navigation = ({
  app,
  expert,
  service,
  category,
  developer,
  showCart = true,
  showSearch = true,
  LogoComponent,
}) => {
  const isAuthorized = useTypedSelector(isUserAuthorized)
  const isInitialized = useTypedSelector(isUserInitialized)
  const hydrated = useHydrationContext()
  const isShopifyInstalled = useParamSelector(isAppInstalled, shopifyAppId)
  const [isSearchExpanded, setIsExpanded] = useState(false)

  const shouldDisplayMobileMenu = useMedia({
    maxWidth: mediaQueries.BASE_MEDIUM_SIZE + 99,
  })

  const isSearchHiddenQueries = {
    minWidth: mediaQueries.BASE_SMALL_SIZE,
    ...(isAuthorized && { maxWidth: mediaQueries.BASE_X_LARGE_SIZE - 1 }),
  }

  const isSearchHidden = useMedia(
    isSearchHiddenQueries,
    !(isAuthorized && window.innerWidth > mediaQueries.BASE_X_LARGE_SIZE - 1)
  )

  const isSearchInputVisible = isSearchHidden ? isSearchExpanded : true

  const expandAllAppsList = () => {
    if (canUseWindow) {
      sessionStorage.setItem(APPS_LIST_STORAGE_KEY, true)
    }
  }

  let pagesItems = [
    {
      to: URLS.APPS.LINK,
      label: 'Apps',
      tag: Link,
      ...(isHelpDesk && { onClick: expandAllAppsList }),
      partiallyActive: true,
      track: true,
    },
    {
      label: URLS.EXPERTS.LABEL,
      to: URLS.EXPERTS.LINK,
      tag: Link,
      track: true,
    },
    {
      label: URLS.DEVELOPERS.LABEL,
      to: URLS.DEVELOPERS.LINK,
      tag: Link,
      track: true,
    },
    {
      label: URLS.SAAS_DEALS.LABEL,
      to: URLS.SAAS_DEALS.LINK,
      tag: Link,
      track: true,
    },
  ]

  if (isHelpDesk) {
    pagesItems = pagesItems
      .filter(({ label }) => label !== URLS.EXPERTS.LABEL)
      .filter(({ label }) =>
        isAuthorized && isShopifyInstalled
          ? label !== URLS.DEVELOPERS.LABEL
          : true
      )
  }

  return (
    <>
      <div css={navigationWrapper} className="navigation-wrapper">
        {LogoComponent ?? (
          <Logo
            label={
              <NavLink
                tag={MPLink}
                to="/"
                label="Marketplace"
                css={logoLabelCss}
              />
            }
          />
        )}
        <div
          css={menuWrapperCss(isSearchHidden && isSearchExpanded)}
          className="pages-menu"
        >
          <PagesMenu
            items={pagesItems}
            data-test="navigation-menu"
            css={pagesMenuCss}
          />
        </div>

        {shouldDisplayMobileMenu &&
          (hydrated ? (
            <div css={mobileActionsWrapper(isAuthorized)}>
              {showSearch && <MobileSearch />}
              {isAuthorized && showCart && <Cart />}
              <MobileMenu />
            </div>
          ) : (
            <div css={mobileActionLoaderCss}>
              <SearchIcon />
              <div className="icon-hamburger">
                <HamburgerIcon />
              </div>
            </div>
          ))}

        {!shouldDisplayMobileMenu && (
          <div css={actionsWrapperCss(hydrated && isAuthorized)}>
            {hydrated && isInitialized ? (
              <div css={actionsContentCss}>
                <Search
                  isInputVisible={isSearchInputVisible}
                  onIsExpanded={setIsExpanded}
                  className="search"
                />

                {isAuthorized && showCart && <Cart />}
                <Account />
              </div>
            ) : (
              <div css={loaderCss}>
                <div className="rectangle" />
                <div className="circle" />
                <div className="circle" />
              </div>
            )}
          </div>
        )}
      </div>

      {hydrated && (
        <BreadcrumbsWrapper
          app={app}
          expert={expert}
          service={service}
          category={category}
          developer={developer}
        />
      )}
    </>
  )
}

Navigation.propTypes = {
  app: object,
  service: object,
  expert: object,
  category: object,
  developer: object,
  showCart: bool,
  showSearch: bool,
  LogoComponent: oneOfType([func, object]),
}

export default Navigation
